<template>
    <p-modal
        name="exportTaxesbyJurisdictionModal"
        label="Sales/Use Tax"
        ref="exportTaxesbyJurisdictionModal"
        size="xl"
        @ok.prevent="handleModalOk"
    >
     <template v-slot:default>
            <p-form ref="taxesByJurisdictionForm">
                <div class="container-fluid content-wrapper">
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="States/Provinces" class="static-height">
                                <p-filter-select
                                    data-type="states"
                                    rules="required"
                                    :initial-display-count="10"
                                    select-all-button
                                    v-model="taxReportParameters.stateIds"
                                />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="POS Devices" class="static-height">
                                <p-filter-select
                                    data-type="posDevicesWithLocation"
                                    rules="required"
                                    :initial-display-count="10"
                                    select-all-button
                                    v-model="taxReportParameters.registerIds"
                                />
                            </p-card>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range ref="dateRange" :key="dateRangeKey" :date-range-options="taxReportParameters" />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Results">
                                <b-row>
                                    <b-col>
                                        <b-form-radio
                                            :value="false"
                                            v-model="taxReportParameters.isDetailed">
                                            Summary (one line per state)
                                        </b-form-radio>
                                    </b-col>
                                </b-row>
                                <b-row class="pt-1">
                                    <b-col>
                                        <b-form-radio
                                            :value="true"
                                            v-model="taxReportParameters.isDetailed">
                                            Detailed (one line per postal code)
                                        </b-form-radio>
                                    </b-col>
                                </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
          <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2"
                        >OK</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    components: { UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Sales/Use Tax',
            // Bumped on every open to force p-date-range to re-mount, resetting
            // its dropdown and dates back to the "Today" default. Without this,
            // the child's selectedRange/fromDate/toDate persist across opens.
            dateRangeKey: 0,
            taxReportParameters: {
                stateIds: [],
                registerIds: [],
                // p-date-range writes the look-back selection into these,
                // matching the Transaction Search date range convention.
                fromDate: null,
                toDate: null,
                isDetailed: false,
                locationId: null,
            }
        };
    },
    computed: {
        pageTitle() {
            return 'Sales/Use Tax';
        },
        // ReportTemplates works against `reportParameters`; this report's parameters object is
        // `taxReportParameters`. Returning it here means the mixin saves and restores the same
        // object the inputs are bound to.
        reportParameters() {
            return this.taxReportParameters;
        }
    },
    methods: {
        async show() {
            // Start with nothing selected; the user picks states/devices (or uses
            // Select All). Reset on every open so reopening clears prior choices.
            this.taxReportParameters.stateIds = [];
            this.taxReportParameters.registerIds = [];
            this.taxReportParameters.isDetailed = false;
            // Force p-date-range to re-initialize so the date range resets too.
            this.dateRangeKey++;
            await this.loadUserReportTemplates();
            this.$refs.exportTaxesbyJurisdictionModal.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The state and POS device ids are the
        // saved references — a disabled or deleted register drops out of its list. The dates and
        // the Summary/Detailed radio are literal values.
        async validateReportParameters(savedParameters) {
            const [states, posDevices] = await Promise.all([
                selectListOptionsDataContext.getStoreDropdownData('states'),
                selectListOptionsDataContext.getStoreDropdownData('posDevicesWithLocation')
            ]);

            const hasValidStates = (savedParameters.stateIds ?? []).every(stateId =>
                states.some(state => state.value == stateId));

            if (!hasValidStates) {
                return false;
            }

            return (savedParameters.registerIds ?? []).every(registerId =>
                posDevices.some(posDevice => posDevice.value == registerId));
        },
        // ReportTemplates hook: p-date-range keeps the Date Range dropdown selection in its own
        // state and disables the pickers unless it reads 'Custom'. Restored dates are specific
        // dates rather than a preset, so switch it over — otherwise the dropdown would still say
        // 'Today' while showing the restored dates, and they could not be edited.
        onUserReportTemplateApplied() {
            this.$refs.dateRange.setCustomRange();
        },
        handleModalOk: async function() {

            if (!(await this.$refs.taxesByJurisdictionForm.validate())) {
                return;
            }
            if (this.taxReportParameters.stateIds.length === 0) {
                this.$appError(
                    'Please select one or more States.'
                );
                return;
            }
            if (this.taxReportParameters.registerIds.length === 0) {
                this.$appError(
                    'Please select one or more Pos Devices.'
                );
                return;
            }

            this.isBusy = true;

            this.taxReportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            await axios
                .post('/sales/exportTaxesByJurisdictionCsv', this.taxReportParameters, {
                    responseType: 'blob'
                })
                .then(result => {
                    const reportType = this.taxReportParameters.isDetailed ? 'Detailed' : 'Summary';
                    downloadFileToBrowser(
                        result.data,
                        `Sales-Use Tax - ${reportType}.csv`,
                        'application/csv'
                    );
                    this.$appSuccess(`Sales/Use Tax ${reportType} report generated successfully.`)
                    this.$refs.exportTaxesbyJurisdictionModal.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    }
};
</script>

<style scoped>
.static-height {
    height:370px
}
</style>
